import { readUtm } from '@theme/attribution';

// CORS is confirmed open for https://astrotalk.store. If this section is
// reused on a different storefront domain, verify that domain is allowed too —
// otherwise the browser silently blocks the fetch with no visible error.
const CITY_AUTOCOMPLETE_URL =
  'https://api.supportchat.astrotalk.com/AstroChat/cities/allcountries/autocomplete';
/**
 * An Indian mobile as ten bare digits, or '' if it isn't one. Mirrors
 * normalizePhone() in the backend, which re-derives this from whatever is
 * stored — the two have to agree or a number that passes here is dropped there.
 */
/**
 * Minor units (paise) to a display string.
 *
 * Always two decimals, because the only place this is rendered is in place of a
 * Liquid `| money` amount ("₹799.00") — dropping a trailing .00 here would make
 * the discounted label read differently from the price it replaced.
 *
 * @param {number} minor
 * @param {string} [currency]
 */
function formatMinor(minor, currency = 'INR') {
  const abs = Math.abs(Math.round(minor));
  const body = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
  const sign = minor < 0 ? '-' : '';
  return currency === 'INR' ? `${sign}₹${body}` : `${sign}${currency} ${body}`;
}

function normalizePhone(raw) {
  const digits = String(raw ?? '').replace(/\D/g, '');
  const local =
    digits.startsWith('91') && digits.length === 12
      ? digits.slice(2)
      : digits.startsWith('0') && digits.length === 11
        ? digits.slice(1)
        : digits;
  return /^[6-9]\d{9}$/.test(local) ? local : '';
}
/**
 * The buyer's email, trimmed, or '' if it isn't one.
 *
 * Deliberately the same shape as the check in api.kundli-details.tsx —
 * something before an @, something after it, and a dot in the domain. Anything
 * stricter rejects addresses that work; anything looser lets through a typo the
 * report can never be delivered to.
 */
function normalizeEmail(raw) {
  const value = String(raw ?? '').trim();
  return /^[^\s@.]+(?:\.[^\s@.]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i.test(value) ? value : '';
}

const POB_DEBOUNCE_MS = 500;
const POB_MIN_CHARS = 3;

/**
 * @param {Function} fn
 * @param {number} wait
 */
function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

function clampDigits(value, max) {
  const digits = value.replace(/\D/g, '').slice(0, 2);
  if (digits === '') return digits;
  const num = Number(digits);
  if (num > max) return String(max);
  return digits;
}

/**
 * The birth-detail keys to clear off the cart. Skip-form pages blank exactly
 * these and nothing else, so third-party cart attributes (GoKwik-Cart,
 * verifast_session, rudderAnonymousId) survive untouched.
 *
 * Every key #buildDetails writes. A stale value left here rides into the next
 * order — dispatch prefers a cart `email` over the address the buyer actually
 * checked out with — and /cart/clear.js empties line items without touching
 * attributes, so clearing them has to be explicit.
 */
const BIRTH_DETAIL_KEYS = ['name', 'gender', 'dob', 'tob', 'pob', 'lat', 'lon', 'number', 'language', 'email'];

/** Converts a 12-hour HH:MM + AM/PM trio to a 24-hour "HH:MM" string. */
function to24Hour(hh, mm, ampm) {
  const hour12 = Number(hh) % 12;
  const hour24 = ampm === 'PM' ? hour12 + 12 : hour12;
  return `${String(hour24).padStart(2, '0')}:${String(Number(mm)).padStart(2, '0')}`;
}

export class KrLandingApp extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('[data-kr-modal]');
    this.form = this.querySelector('[data-kr-form]');
    this.submitButton = this.querySelector('[data-kr-submit]');
    this.formError = this.querySelector('[data-kr-form-error]');
    this.pobWrapper = this.querySelector('[data-kr-pob-wrapper]');
    this.pobInput = this.querySelector('[data-kr-field="pob"]');
    this.autocompleteList = this.querySelector('[data-kr-autocomplete-list]');
    this.timeRow = this.querySelector('[data-kr-time-row]');
    this.hhInput = this.querySelector('[data-kr-field="hh"]');
    this.mmInput = this.querySelector('[data-kr-field="mm"]');
    this.unknownTimeCheckbox = this.querySelector('[data-kr-unknown-time]');
    this.unknownTimeNote = this.querySelector('[data-kr-unknown-note]');
    this.stickyBar = this.querySelector('[data-kr-sticky-bar]');
    // Bypass pages only — the markup is behind the same enable_bypass check, so
    // these are null on a page that goes to Shopify's own checkout.
    this.discountInput = this.querySelector('[data-kr-discount-input]');
    this.discountApplyButton = this.querySelector('[data-kr-discount-apply]');
    this.discountMessage = this.querySelector('[data-kr-discount-message]');
    /**
     * Set only by a server-verified apply-discount response. Null means "no
     * discount", and create-order is told the code — never an amount — so a
     * tampered value here buys nothing.
     *
     * @type {{ code: string, subtotalMinor: number, totalMinor: number, discountMinor: number, currency: string } | null}
     */
    this.discount = null;
    this.discountBusy = false;
    /**
     * True from the moment a purchase starts until it ends. `disabled` alone is
     * not a guard: #validate() rewrites it on every field edit, so a buyer who
     * types during the create-order round trip would get a live button back.
     */
    this.submitting = false;

    this.selectedGender = null;
    this.selectedLanguage = '1';
    this.selectedAmPm = 'AM';
    this.selectedLat = '';
    this.selectedLon = '';
    this.lastPickedPobText = null;
    this.pobRequestSeq = 0;
    this.activeVariantId = this.dataset.variantId || '';
    this.skipForm = this.dataset.skipForm === 'true';

    // On skip-form pages the modal is never rendered, so every form binding
    // below would query a null element. Wire up only the page-level behaviour
    // and send CTAs straight to checkout.
    if (this.skipForm) {
      this.#bindDirectCheckoutButtons();
      this.#bindFaqAccordion();
      this.#bindSmoothScroll();
      this.#bindStickyBar();
      return;
    }

    this.#bindOpenButtons();
    this.#bindModal();
    this.#bindGenderToggle();
    this.#bindLanguageToggle();
    this.#bindAmPmToggle();
    this.#bindTimeInputs();
    this.#bindUnknownTime();
    this.#bindPlaceAutocomplete();
    this.#bindFaqAccordion();
    this.#bindSmoothScroll();
    this.#bindStickyBar();
    this.#bindFormValidation();
    this.#bindSubmit();
    this.#bindDiscount();
  }

  // ---------- Modal open/close ----------

  #bindOpenButtons() {
    this.querySelectorAll('[data-kr-open-modal]').forEach((button) => {
      button.addEventListener('click', () => this.openModal(button.dataset.variantId));
    });
  }

  // ---------- Direct checkout (skip-form pages) ----------

  #bindDirectCheckoutButtons() {
    this.querySelectorAll('[data-kr-open-modal]').forEach((button) => {
      button.addEventListener('click', () => this.#directCheckout(button, button.dataset.variantId));
    });
  }

  /**
   * Buys the product with no birth details attached.
   *
   * Cart attributes are CART-level and outlive the line items that created
   * them, so a customer who filled the form on the other kundli page (and
   * abandoned) would otherwise carry dob/pob/etc. into this order. Fulfilment
   * reads those attributes, sees the details already present, skips the
   * collection step and never dispatches to the Kundli API — the customer pays
   * and receives nothing, with no error anywhere. /cart/clear.js only zeroes
   * line items, NOT attributes, so they must be blanked explicitly ('' removes
   * a key).
   */
  async #directCheckout(button, variantId) {
    const activeVariantId = variantId || this.dataset.variantId || '';
    if (!activeVariantId || button.disabled) return;
    // Page-level, not per-button: these pages render the CTA four times (hero,
    // mid-page, final, sticky bar) and `button.disabled` only blocks the one
    // that was clicked. Without this, tapping a second CTA while the first is
    // still working runs the whole flow again — /cart/clear.js wipes the line
    // the first run is holding a key for, and two Razorpay orders open.
    if (this.submitting) return;
    this.submitting = true;

    const originalLabel = button.textContent;
    button.disabled = true;
    // Tell them something is happening; a silently greyed-out button is what
    // makes people go tap a different one.
    button.textContent = 'Processing…';

    try {
      await fetch('/cart/clear.js', { method: 'POST' });

      const blanked = {};
      BIRTH_DETAIL_KEYS.forEach((key) => {
        blanked[key] = '';
      });
      const clearResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: blanked }),
      });
      if (!clearResponse.ok) throw new Error('attribute-clear failed');

      // The response of /cart/update.js IS the updated cart, so verifying the
      // wipe costs no extra round trip. Refuse to send a poisoned cart to
      // checkout: a blocked sale is recoverable, a paid-but-undelivered is not.
      const cart = await clearResponse.json();
      const leftover = BIRTH_DETAIL_KEYS.filter((key) => {
        const value = cart?.attributes?.[key];
        return value !== undefined && value !== null && String(value).trim() !== '';
      });
      if (leftover.length) throw new Error(`birth details still on cart: ${leftover.join(', ')}`);

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(activeVariantId), quantity: 1 }] }),
      });
      if (!addResponse.ok) throw new Error('add-to-cart failed');

      // Checkout-bypass: same inline Razorpay payment the form pages use, just
      // with no details to collect — the item is already in the cart above, so
      // this is add-to-cart then pay, exactly like the form path. No discount
      // box here: these pages render no form to put one in.
      if (this.dataset.bypass) {
        // #completeBypass prices this.activeVariantId, and a CTA may carry its
        // own data-variant-id — pin it to the one actually added to the cart so
        // the two can never disagree about what is being sold.
        this.activeVariantId = activeVariantId;

        // The page has no birth-details sheet, submit button or inline error
        // slot, so the bypass is given a view built around the CTA itself.
        const added = await addResponse.json().catch(() => null);
        const line = added?.items?.[0] ?? added;

        await this.#completeBypass(null, null, originalLabel, {
          reset: () => {
            this.submitting = false;
            button.disabled = false;
            button.textContent = originalLabel;
          },
          showError: (message) => window.alert(message),
          // Nothing to reopen — reset() alone puts the CTA back.
          reopen: () => {},
          // Nothing to close — no sheet was ever opened.
          close: () => {},
          cartLineKey: line?.key ?? null,
        });
        return;
      }

      window.location.href = '/checkout';
    } catch (error) {
      this.submitting = false;
      button.disabled = false;
      button.textContent = originalLabel;
      window.alert('Something went wrong preparing your order. Please refresh the page and try again.');
    }
  }

  #bindModal() {
    if (!this.modal) return;

    this.querySelectorAll('[data-kr-close-modal]').forEach((button) => {
      button.addEventListener('click', () => this.closeModal());
    });

    this.modal.addEventListener('click', (event) => {
      if (event.target === this.modal) this.closeModal();
    });

    // Fires on every close path (explicit button, backdrop click, native Escape),
    // so the place dropdown never lingers open behind a closed modal and any
    // still-in-flight autocomplete request is ignored when it resolves.
    this.modal.addEventListener('close', () => {
      this.#hideAutocomplete();
      this.pobRequestSeq += 1;
    });
  }

  openModal(variantId) {
    this.activeVariantId = variantId || this.dataset.variantId || '';

    if (this.formError) {
      this.formError.hidden = true;
      this.formError.textContent = '';
    }

    // Re-assert the validation-based disabled state every time the modal
    // opens, in case a site-wide script force-enabled the shared button id/class.
    this.#validate();

    if (typeof this.modal.showModal === 'function') {
      this.modal.showModal();
    } else {
      this.modal.setAttribute('open', '');
    }
  }

  closeModal() {
    if (typeof this.modal.close === 'function') {
      this.modal.close();
    } else {
      this.modal.removeAttribute('open');
    }
  }

  // ---------- Gender / AM-PM pill toggles ----------

  #bindGenderToggle() {
    const buttons = this.querySelectorAll('[data-kr-gender]');
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        buttons.forEach((b) => b.classList.remove('kr-pill-toggle__btn--active'));
        button.classList.add('kr-pill-toggle__btn--active');
        this.selectedGender = button.dataset.krGender;
        this.#validate();
      });
    });
  }

  #bindLanguageToggle() {
    const select = this.querySelector('[data-kr-field="language"]');
    if (!select) return;
    // Matches the select to the default rather than trusting the markup, so
    // a browser restoring a previous pick can't leave the two out of step.
    this.selectedLanguage = select.value;
    select.addEventListener('change', () => {
      this.selectedLanguage = select.value;
    });
  }

  #bindAmPmToggle() {
    const buttons = this.querySelectorAll('[data-kr-ampm]');
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        buttons.forEach((b) => b.classList.remove('kr-pill-toggle__btn--active'));
        button.classList.add('kr-pill-toggle__btn--active');
        this.selectedAmPm = button.dataset.krAmpm;
        this.#validate();
      });
    });
  }

  // ---------- Time of birth ----------

  #bindTimeInputs() {
    if (this.hhInput) {
      this.hhInput.addEventListener('input', () => {
        this.hhInput.value = clampDigits(this.hhInput.value, 12);
        this.#validate();
      });
    }
    if (this.mmInput) {
      this.mmInput.addEventListener('input', () => {
        this.mmInput.value = clampDigits(this.mmInput.value, 59);
        this.#validate();
      });
    }
  }

  #bindUnknownTime() {
    if (!this.unknownTimeCheckbox) return;

    this.unknownTimeCheckbox.addEventListener('change', () => {
      const isChecked = this.unknownTimeCheckbox.checked;

      if (this.timeRow) {
        this.timeRow.toggleAttribute('data-kr-time-disabled', isChecked);
        [this.hhInput, this.mmInput, ...this.timeRow.querySelectorAll('[data-kr-ampm]')].forEach((el) => {
          if (el) el.disabled = isChecked;
        });
      }

      if (this.unknownTimeNote) this.unknownTimeNote.hidden = !isChecked;

      this.#validate();
    });
  }

  // ---------- Place of birth autocomplete ----------

  #bindPlaceAutocomplete() {
    if (!this.pobInput || !this.autocompleteList) return;

    const search = debounce(() => this.#searchPlaces(), POB_DEBOUNCE_MS);

    this.pobInput.addEventListener('input', () => {
      // A suggestion click sets .value via JS, which never fires a native
      // "input" event — so every "input" here is a genuine hand-edit, and
      // any previously-captured lat/lon no longer necessarily matches.
      if (this.lastPickedPobText !== null) {
        this.selectedLat = '';
        this.selectedLon = '';
        this.lastPickedPobText = null;
      }

      this.#validate();

      const query = this.pobInput.value.trim();
      if (query.length < POB_MIN_CHARS) {
        // Invalidate the sequence so a slower in-flight response for the
        // longer query can't land and render after the user deleted back down.
        this.pobRequestSeq += 1;
        this.#hideAutocomplete();
        return;
      }
      search();
    });

    document.addEventListener('click', (event) => {
      if (!this.pobWrapper.contains(event.target)) this.#hideAutocomplete();
    });
  }

  async #searchPlaces() {
    const query = this.pobInput.value.trim();
    if (query.length < POB_MIN_CHARS) return;

    const requestId = ++this.pobRequestSeq;

    let results = [];
    try {
      const response = await fetch(
        `${CITY_AUTOCOMPLETE_URL}?limit=10&key=${encodeURIComponent(query)}`
      );
      if (response.ok) {
        const json = await response.json();
        results = Array.isArray(json?.data) ? json.data : [];
      } else {
        // Never surface this to the customer (the field just behaves like
        // plain text), but log it — a non-ok response here (e.g. 403) usually
        // means this page's origin isn't on the API's CORS allow-list.
        console.warn(`[kr-landing] place autocomplete request failed: HTTP ${response.status}`);
      }
    } catch (error) {
      results = [];
      console.warn('[kr-landing] place autocomplete request errored', error);
    }

    // A slower earlier request must not overwrite a result from a newer keystroke.
    if (requestId !== this.pobRequestSeq) return;

    this.#renderAutocomplete(results);
  }

  #renderAutocomplete(results) {
    if (!results.length) {
      this.#hideAutocomplete();
      return;
    }

    this.autocompleteList.innerHTML = '';
    results.forEach((place) => {
      const label = [place.name, place.state, place.countryName].filter(Boolean).join(', ');
      const item = document.createElement('li');
      item.textContent = label;
      item.addEventListener('click', () => {
        this.pobInput.value = label;
        this.selectedLat = place.latitude ?? '';
        this.selectedLon = place.longitude ?? '';
        this.lastPickedPobText = label;
        this.#hideAutocomplete();
        this.#validate();
      });
      this.autocompleteList.appendChild(item);
    });
    this.autocompleteList.hidden = false;
  }

  #hideAutocomplete() {
    this.autocompleteList.hidden = true;
    this.autocompleteList.innerHTML = '';
  }

  // ---------- FAQ accordion ----------

  #bindFaqAccordion() {
    this.querySelectorAll('[data-kr-faq-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }

  // ---------- Smooth scroll ----------

  #bindSmoothScroll() {
    this.querySelectorAll('[data-kr-scroll-to]').forEach((link) => {
      link.addEventListener('click', (event) => {
        // Scoped to this component instance (not document-wide) so this
        // still finds the right target even if the same section renders
        // more than once on a page (e.g. across different products), where
        // a shared/duplicated id could otherwise resolve to the wrong one.
        const target = this.querySelector(link.dataset.krScrollTo);
        if (!target) return;
        event.preventDefault();

        const headerHeight =
          parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0;
        const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;

        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  }

  // ---------- Sticky mobile bar ----------

  #bindStickyBar() {
    if (!this.stickyBar) return;

    const heroCta = this.querySelector('.kr-hero [data-kr-open-modal]');
    if (!heroCta || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        this.stickyBar.hidden = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    observer.observe(heroCta);
  }

  // ---------- Validation ----------

  #bindFormValidation() {
    ['name', 'number', 'email', 'dob'].forEach((key) => {
      const field = this.querySelector(`[data-kr-field="${key}"]`);
      if (field) field.addEventListener('input', () => this.#validate());
    });
    this.#validate();
  }

  #isTimeValid() {
    if (this.unknownTimeCheckbox?.checked) return true;
    const hh = this.hhInput?.value.trim();
    const mm = this.mmInput?.value.trim();
    return Boolean(hh && mm);
  }

  /** True once a suggestion has been clicked and the field hasn't been edited since. */
  #hasPickedPlace() {
    return String(this.selectedLat ?? '') !== '' && String(this.selectedLon ?? '') !== '';
  }

  #validate() {
    const name = this.querySelector('[data-kr-field="name"]')?.value.trim() || '';
    const number = this.querySelector('[data-kr-field="number"]')?.value.trim() || '';
    const email = this.querySelector('[data-kr-field="email"]')?.value.trim() || '';
    const dob = this.querySelector('[data-kr-field="dob"]')?.value || '';
    const pob = this.pobInput?.value.trim() || '';

    const isValid = Boolean(
      name.length > 1 &&
        this.selectedGender &&
        normalizePhone(number) !== '' &&
        normalizeEmail(email) !== '' &&
        dob &&
        dob <= new Date().toISOString().slice(0, 10) &&
        this.#isTimeValid() &&
        pob.length > 1 &&
        // The place must have been picked from the suggestions, not typed.
        // Hand-editing clears selectedLat/selectedLon, so free text submits
        // with empty coordinates — a paid order that can never generate a
        // report, because nothing downstream can recover them from a name
        // alone. Order #1038 typed "Pune" and did exactly that.
        this.#hasPickedPlace()
    );

    // Never hand the button back while a purchase is in flight. #validate runs
    // on every field edit, so without this a buyer who touches a field during
    // the create-order round trip gets a live button and can start a SECOND
    // Razorpay order (and a second cart line) with another tap.
    if (this.submitButton) this.submitButton.disabled = !isValid || this.submitting;
    return isValid;
  }

  // ---------- Submit ----------

  #bindSubmit() {
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#handleSubmit();
    });
  }

  #buildDetails() {
    const name = this.querySelector('[data-kr-field="name"]').value.trim();
    const number = this.querySelector('[data-kr-field="number"]').value.trim();
    const email = this.querySelector('[data-kr-field="email"]').value.trim();
    const dob = this.querySelector('[data-kr-field="dob"]').value;
    const pob = this.pobInput.value.trim();

    // Midnight in 24-hour form, not "12:00am". The report API parses times
    // with a strict HH:MM layout and rejects the 12-hour string *after*
    // answering 200, so a "12:00am" order looked dispatched and silently
    // produced nothing — five of them before it was spotted.
    const tob = this.unknownTimeCheckbox?.checked
      ? '00:00'
      : to24Hour(this.hhInput.value.trim(), this.mmInput.value.trim(), this.selectedAmPm);

    return {
      name,
      gender: this.selectedGender,
      language: this.selectedLanguage,
      dob,
      tob,
      pob,
      // lastPickedPobText/selectedLat/selectedLon are always cleared together
      // the instant the field is hand-edited (see #bindPlaceAutocomplete), so
      // selectedLat/selectedLon alone already reflect "picked and still valid".
      lat: String(this.selectedLat ?? ''),
      lon: String(this.selectedLon ?? ''),
      // Stored normalised, not as typed. This is the one write path with no
      // server in front of it — the details go straight to /cart/add.js — so
      // "+91 98155 00123" would land on the order verbatim, while variant B's
      // identical input arrives as "9815500123" because api.kundli-details
      // normalises it. #validate() already rejects anything normalizePhone
      // can't resolve, so this cannot be ''.
      number: normalizePhone(number),
      // Normalised for the same reason as the phone: this write path has no
      // server in front of it, so whatever lands here lands on the order.
      // #validate() already rejects anything normalizeEmail can't resolve.
      email: normalizeEmail(email),
    };
  }

  async #handleSubmit() {
    // Reentrancy guard first: a double-tap on a slow connection would otherwise
    // run this whole flow twice and create two Razorpay orders.
    if (this.submitting) return;
    if (!this.#validate()) return;
    if (!this.activeVariantId) {
      this.#showError('This product is not available right now. Please try again later.');
      return;
    }

    const details = this.#buildDetails();
    const submitLabel = this.submitButton.querySelector('[data-kr-submit-label]');
    const originalLabel = submitLabel ? submitLabel.textContent : '';

    this.submitting = true;
    this.submitButton.disabled = true;
    if (submitLabel) submitLabel.textContent = 'Loading..';
    if (this.formError) this.formError.hidden = true;

    // Checkout-bypass: take payment inline via the checkout-bypass app + Razorpay
    // and let the app create the paid order server-side (with these details as
    // line-item properties + order attributes), instead of cart-add → Shopify
    // checkout. Enabled per-page via data-bypass so only wired-up pages use it.
    if (this.dataset.bypass) {
      await this.#completeBypass(details, submitLabel, originalLabel);
      return;
    }

    try {
      await fetch('/cart/clear.js', { method: 'POST' });

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(this.activeVariantId), quantity: 1, properties: details }],
        }),
      });
      if (!addResponse.ok) throw new Error('add-to-cart failed');

      // Every key we own, so a value left on the cart by an earlier session is
      // overwritten rather than merged around. /cart/update.js only touches the
      // keys it is given, and /cart/clear.js does not clear attributes at all —
      // so a stale `email` would ride into this order, where dispatch prefers
      // it over the address the buyer actually checked out with.
      const cartAttributes = {};
      BIRTH_DETAIL_KEYS.forEach((key) => {
        cartAttributes[key] = details[key] ?? '';
      });

      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: cartAttributes }),
      });
      if (!updateResponse.ok) throw new Error('cart-update failed');

      window.location.href = '/checkout';
    } catch (error) {
      this.#showError("Something went wrong — please try again.");
      this.submitting = false;
      this.submitButton.disabled = false;
      if (submitLabel) submitLabel.textContent = originalLabel;
    }
  }

  // ---------- Cart (bypass pages) ----------

  /**
   * Adds the item being bought to the Shopify cart, so the cart badge and any
   * cart-based analytics see it the way they would on a normal Buy Now.
   *
   * Not part of pricing: create-order has already fixed the amount server-side
   * and the paid Shopify order is built from that, so nothing here can change
   * what the buyer is charged. Which is also why every failure is swallowed —
   * a cart hiccup must never block a payment that is otherwise ready.
   *
   * Returns the new line's `key` so it can be removed again after payment, or
   * null if the add didn't happen.
   *
   * @param {Record<string, string>} details
   * @returns {Promise<string | null>}
   */
  async #addToCart(details) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(this.activeVariantId), quantity: 1, properties: details }],
        }),
      });
      if (!res.ok) return null;

      // /cart/add.js answers with the added line(s); `key` is what
      // /cart/change.js needs to target this exact line later.
      const added = await res.json();
      const line = added?.items?.[0] ?? added;
      // Not awaited: the badge is decoration and must not delay opening
      // Razorpay, which is what the buyer is waiting for.
      void this.#refreshCartBadge();
      return line?.key ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Repaints the header cart badge from the real cart.
   *
   * The badge only moves when the theme dispatches its own cartLinesUpdate
   * event (see assets/cart-icon.js), and a bare fetch to /cart/add.js does not
   * — so without this the item is genuinely in the cart but the icon still
   * reads 0. Reads the authoritative count rather than guessing, and drives
   * cart-icon's own render so the animation and sessionStorage bookkeeping
   * stay exactly as the theme expects.
   *
   * Purely cosmetic: every failure is swallowed.
   */
  async #refreshCartBadge() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const icon = document.querySelector('cart-icon');
      if (icon && typeof icon.renderCartBubble === 'function') {
        icon.renderCartBubble(cart.item_count ?? 0);
      }
    } catch {
      /* The badge is decoration; never let it affect a purchase. */
    }
  }

  /**
   * Removes one line from the cart by key, leaving the rest of the cart intact.
   *
   * @param {string | null} key
   */
  #removeCartLine(key) {
    if (!key) return;
    // No await anywhere upstream: this runs while the buyer is being redirected
    // to their order-status page, and must not delay that.
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    })
      .then(() => this.#refreshCartBadge())
      .catch(() => {
        /* The order is already paid and created; a leftover cart line is a
           cosmetic annoyance, not something to surface to the buyer. */
      });
  }

  // ---------- Discount code (bypass pages only) ----------

  /**
   * Wires the coupon box.
   *
   * Applying is a QUOTE, not a charge: the app asks Shopify's own calculator
   * what the code is worth and creates nothing. Nothing shown here is trusted
   * at payment time — #completeBypass sends the code STRING alone and the
   * server re-runs the identical calculation before the Razorpay order exists,
   * so editing this in devtools changes the display and nothing else.
   */
  #bindDiscount() {
    if (!this.discountApplyButton || !this.discountInput) return;

    this.discountApplyButton.addEventListener('click', () => this.#applyDiscount());

    this.discountInput.addEventListener('keydown', (event) => {
      // The box lives inside the details <form>, so a bare Enter would submit
      // the whole form and start a payment at the undiscounted price.
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#applyDiscount();
      }
    });

    // Editing after applying invalidates the quote — the button must never
    // promise a price that no longer matches what is in the box.
    this.discountInput.addEventListener('input', () => {
      if (this.discount) this.#clearDiscount();
    });
  }

  async #applyDiscount() {
    const code = String(this.discountInput.value || '').trim();
    if (!code || this.discountBusy) return;

    const proxyRoot = (this.dataset.proxyRoot || '').replace(/\/+$/, '');
    if (!proxyRoot) return;

    this.discountBusy = true;
    this.discountApplyButton.disabled = true;
    const originalApplyLabel = this.discountApplyButton.textContent;
    this.discountApplyButton.textContent = 'Checking…';
    this.discountMessage.hidden = true;

    try {
      const res = await fetch(`${proxyRoot}/apply-discount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          path: this.dataset.pagePath || location.pathname,
          variantId: this.activeVariantId,
          quantity: 1,
          code,
        }),
      });

      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error('Could not check that code. Please try again.');
      }
      if (!res.ok || data.error) throw new Error(data.error || "That code isn't valid");

      // A 200 that is missing these would render "Pay ₹NaN.NaN" on the button
      // and send the buyer to Razorpay against a price nobody can read.
      if (!Number.isFinite(data.totalMinor) || !Number.isFinite(data.discountMinor)) {
        throw new Error('Could not check that code. Please try again.');
      }

      this.discount = data;
      this.#setDiscountMessage(
        `${data.code} applied — ${formatMinor(data.discountMinor, data.currency)} off`,
        'ok',
      );
    } catch (error) {
      this.discount = null;
      this.#setDiscountMessage(error?.message || "That code isn't valid", 'error');
    } finally {
      this.discountBusy = false;
      this.discountApplyButton.disabled = false;
      this.discountApplyButton.textContent = originalApplyLabel;
      this.#updateSubmitPrice();
    }
  }

  #clearDiscount() {
    this.discount = null;
    if (this.discountMessage) this.discountMessage.hidden = true;
    this.#updateSubmitPrice();
  }

  /**
   * @param {string} text
   * @param {'ok' | 'error'} kind
   */
  #setDiscountMessage(text, kind) {
    if (!this.discountMessage) return;
    this.discountMessage.textContent = text;
    this.discountMessage.className = `kr-discount__message kr-discount__message--${kind}`;
    this.discountMessage.hidden = false;
  }

  /**
   * Rewrites the submit label to the discounted price.
   *
   * The original label is Liquid-rendered ("Pay ₹799 & Generate My Kundli") and
   * is captured once on the first call, so repeated apply/clear cycles always
   * rebuild from the original rather than from an already-rewritten string.
   */
  #updateSubmitPrice() {
    const label = this.submitButton?.querySelector('[data-kr-submit-label]');
    if (!label) return;

    if (this.originalSubmitLabel === undefined) {
      this.originalSubmitLabel = label.textContent || '';
    }

    if (!this.discount) {
      label.textContent = this.originalSubmitLabel;
      return;
    }

    // Only the amount changes; the surrounding copy is whatever the merchant
    // configured, so it is preserved by substituting the first money-looking
    // run rather than rebuilding the sentence here.
    const paying = formatMinor(this.discount.totalMinor, this.discount.currency);
    const replaced = this.originalSubmitLabel.replace(/₹\s?[\d,]+(?:\.\d{2})?/, paying);
    label.textContent =
      replaced === this.originalSubmitLabel
        ? `Pay ${paying} & Generate My Kundli`
        : replaced;
  }

  /**
   * Checkout-bypass path. Instead of cart-add → Shopify checkout, ask the
   * checkout-bypass app to create a Razorpay order, open the Razorpay modal
   * inline, and on success let the app create the paid Shopify order — with the
   * birth details attached as line-item properties + order attributes. This
   * theme never calls the report API; admin-plus's sweep reads those attributes
   * and dispatches the report. Enabled per-page via data-bypass.
   *
   * @param {Record<string, string>} details
   * @param {Element | null} submitLabel
   * @param {string} originalLabel
   */
  async #completeBypass(details, submitLabel, originalLabel, view = null) {
    const proxyRoot = (this.dataset.proxyRoot || '').replace(/\/+$/, '');
    const pagePath = this.dataset.pagePath || location.pathname;
    const successUrl = this.dataset.successUrl || '/pages/order-history';

    // How this talks to the buyer. Defaults to the birth-details sheet; a
    // skip-form page has no sheet, no submit button and no inline error slot
    // (all null there), so that caller supplies its own.
    const ui = view || {
      reset: () => {
        // Clear the guard BEFORE re-enabling, so #validate() (which now also
        // reads it) can hand the button back.
        this.submitting = false;
        this.submitButton.disabled = false;
        if (submitLabel) submitLabel.textContent = originalLabel;
      },
      showError: (/** @type {string} */ message) => this.#showError(message),
      // Put the sheet back so they can retry with their answers intact.
      reopen: () => this.openModal(this.activeVariantId),
      // Close it first, or it sits on top of the Razorpay modal.
      close: () => this.closeModal(),
      // undefined (not null) means "this caller hasn't added a cart line" — the
      // form path adds its own below. Distinct from null, which a skip-form
      // caller uses to say "I tried and got no key".
      cartLineKey: undefined,
    };

    if (!proxyRoot) {
      ui.showError('Payment is not configured for this page.');
      ui.reset();
      return;
    }
    if (!window.Razorpay) {
      ui.showError('Payment is still loading. Please try again in a moment.');
      ui.reset();
      return;
    }

    /** @param {string} path @param {object} body */
    const post = async (path, body) => {
      const res = await fetch(`${proxyRoot}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error('Payment service returned an unexpected response.');
      }
      if (!res.ok || data.error) throw new Error(data.error || 'Could not start payment.');
      return data;
    };

    try {
      // variantId is not sent: the app resolves the product (and price) from the
      // page-path mapping, so price can never be set from the client.
      const order = await post('create-order', {
        path: pagePath,
        quantity: 1,
        // The page's own product, straight from this section's product picker.
        // The app prices it live server-side; the browser never sends a price.
        // With this, no per-page mapping is needed — each page sells its own
        // product. hasForm tells the app to attach details (admin-plus dispatch).
        variantId: this.activeVariantId || this.dataset.variantId || undefined,
        // No details on a skip-form page, so the app uses its payment-only
        // handler (take the money, create the order) instead of attaching
        // birth details for admin-plus to dispatch.
        hasForm: Boolean(details),
        form: details ? { reports: [details] } : {},
        // Campaign attribution. This flow never reaches Shopify's checkout, so
        // Shopify never derives it — the app writes these onto the order itself.
        utm: readUtm(),
        // The code STRING only — the server re-verifies it against Shopify and
        // recomputes the amount before the Razorpay order exists, so the quote
        // shown on this page is never what gets charged.
        discountCode: this.discount ? this.discount.code : null,
      });

      // Put the item in the Shopify cart so the cart badge and cart-based
      // analytics/abandoned-cart flows see it, matching what a normal Buy Now
      // does. Purely presentational as far as money goes: the price was already
      // fixed by create-order above, and the paid Shopify order is built
      // server-side from that, never from the cart. Best-effort for the same
      // reason — a cart failure must not block a payment that is ready to go.
      //
      // The skip-form caller has already added the line (it needs the cart
      // wiped and rebuilt first), and hands its key over rather than adding a
      // second one.
      const cartLineKey = ui.cartLineKey !== undefined
        ? ui.cartLineKey
        : await this.#addToCart(details);

      const razorpay = new window.Razorpay({
        key: order.razorpayKeyId,
        order_id: order.razorpayOrderId,
        amount: order.amountMinor,
        currency: order.currency,
        name: order.productTitle || this.dataset.productTitle || '',
        description: order.productTitle || '',
        // Empty on a skip-form page — nothing was collected to prefill with, so
        // Razorpay asks for the contact details itself.
        prefill: {
          name: details?.name || '',
          email: details?.email || '',
          contact: details?.number || '',
        },
        notes: { refId: order.refId },
        handler: (response) => {
          // Paid. Ask the app to verify + create the order; even if this call
          // fails, Razorpay's webhook confirms independently, so we still send
          // the buyer on rather than alarming someone who has already paid.
          if (submitLabel) submitLabel.textContent = 'Confirming..';
          // Full-screen loader so the ~1-2s until redirect (order is being
          // created server-side) reads as intentional, not a frozen page. Left in
          // place until the redirect navigates away.
          const overlay = document.createElement('div');
          overlay.setAttribute(
            'style',
            'position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;' +
              'align-items:center;justify-content:center;gap:16px;background:rgba(255,255,255,0.96);' +
              'font-family:inherit;text-align:center;padding:24px;'
          );
          overlay.innerHTML =
            '<div style="width:38px;height:38px;border:3px solid #e5e5e5;border-top-color:#111;' +
            'border-radius:50%;animation:cb-spin 0.8s linear infinite"></div>' +
            '<div style="font-size:16px;color:#111">Payment received — setting up your order…</div>' +
            '<style>@keyframes cb-spin{to{transform:rotate(360deg)}}</style>';
          document.body.appendChild(overlay);

          // The buyer has paid, so the cart has served its purpose — take the
          // line back out rather than leaving them holding an item they already
          // own. Only OUR line, by key, so anything else in their cart is left
          // alone. Fire-and-forget: the redirect below must not wait on it.
          this.#removeCartLine(cartLineKey);

          post('verify-payment', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          })
            .then((res) => {
              // Land on the order's status page when the app returns one (order
              // created inline — the common case); otherwise the configured
              // fallback (creation was deferred to the worker).
              window.location.href = (res && res.statusUrl) || successUrl;
            })
            .catch(() => {
              // Payment is safe (the webhook confirms independently), so send
              // them on rather than alarming someone who has already paid.
              window.location.href = successUrl;
            });
        },
        modal: {
          ondismiss: () => {
            // Take the line back out. Left in, the buyer walks away with an
            // unpaid item in their cart and can check out through Shopify's
            // own checkout later — paying a second time for what they may
            // have already bought here.
            this.#removeCartLine(cartLineKey);
            // Restore the button, and put the birth-details sheet back (answers
            // preserved) where there is one, so they can retry.
            ui.reset();
            ui.reopen();
            ui.showError('Payment was not completed. Press Proceed to Pay to try again.');
          },
        },
      });

      razorpay.on('payment.failed', (response) => {
        this.#removeCartLine(cartLineKey);
        ui.reset();
        ui.reopen();
        ui.showError(response?.error?.description || 'The payment did not go through.');
      });

      // Close our birth-details sheet before the Razorpay modal opens, otherwise
      // it sits on top of Razorpay. The normal flow closed it by navigating to
      // /checkout; the bypass stays on the page, so close it explicitly. A
      // no-op on skip-form pages, where there is no sheet.
      ui.close();
      razorpay.open();
    } catch (error) {
      ui.showError(error?.message || 'Something went wrong — please try again.');
      ui.reset();
    }
  }

  #showError(message) {
    if (!this.formError) return;
    this.formError.textContent = message;
    this.formError.hidden = false;
  }
}

if (!customElements.get('kr-landing-app')) customElements.define('kr-landing-app', KrLandingApp);
